import React, { useMemo } from 'react'
import { useParamsInt } from '../../../../../shared/hooks/location'
import { Route, useRouteMatch } from 'react-router-dom/cjs/react-router-dom.min'
import { BreadcrumbView } from '../../../../../context/breadcrumb.context'
import { NodeCourseDetails } from './pages/node-course-details'
import { IconAdd, IconCategory, IconContent, IconSearch } from '../../../../../shared/icon/icon'
import { NodeContentGridItem } from './node-content-grid-item'
import { CardFullHeight } from '../../../../../shared/card/card-full-height'
import { NodeCourseAdd } from './pages/node-course-add'
import { NodeCategory } from '../node-category/node-category'
import { NodeCategoryGridItem } from '../node-category/node-category-grid-item'
import { FOLDER_SECTION, FolderMenu } from '../common/menu/node-actions'
import { CourseActionsHost } from '../common/menu/course-actions-host'
import {
  useApiFormNodeCourse,
  useApiFormNodeCourseCategory,
  useApiListNodeCourseCategory,
  useApiListNodeDecendantContent
} from '../../../../../api-new/state/content'
import { ListError } from '../common/search/list-error'
import { platformLabel } from '../common/platform/platforms'
import { formatLabel, keepFormat } from '../common/format/formats'
import { useFormatFilter } from '../common/format/format-controls'
import { NodeSearchArea } from '../common/search/node-search-area'
import { CONTENT_SEARCH, hasFormat } from '../common/search/search-areas'
import { NodeContentGridItemVideo } from './node-content-grid-item-video'
import { buildContentCategoryPath } from '../common/shared/build-path'
import { DataProvider, useDataContext } from './data-context'
import { NestedNodeAddress } from '../common/shared/nested-path'
import { folderNotFound } from '../common/shared/node-not-found'
import { notFoundText } from '../../../../../shared/page/record-view'
import { NodeGridBody } from '../common/search/node-grid-body'
import { useLoginSysAdmin } from '../../../../../role/hook/login-sys-admin'
import { useRoleOrgNode } from '../../../../../role/hook/role-org'
import { SECTION } from '../../../../../shared/navigation/sections'

export const NodeContentGrid = () => {
  return (
    <DataProvider>
      {/* A course or folder opened by its id alone goes under its folders. */}
      <NestedNodeAddress useData={useDataContext} />
      {/* Deep link and Launch for every course menu in the area. */}
      <CourseActionsHost>
        <div className="container-fluid p-0 h-100">
          <NodeContentGridBranch />
        </div>
      </CourseActionsHost>
    </DataProvider>
  )
}

const NodeContentGridBranch = ({ grandParentURL }) => {
  const { url } = useRouteMatch()
  const { parentID } = useParamsInt()

  const dataContext = useDataContext()

  const category = useMemo(() => dataContext.categories.find(i => i.id === parentID), [dataContext, parentID])

  // "Available on": with a platform chosen, this level shows content's
  // answer for it -- the courses available there and the folders above
  // them -- so a folder holding one, at any depth, can still be opened.
  const platform = dataContext.platform || null
  const platformView = platform && dataContext.filtered ? dataContext.filtered : dataContext
  const loaded = platform && dataContext.filtered ? dataContext.filtered.loaded : dataContext.loaded

  // "Format" ("v2:", ...): that format's courses and the folders above them,
  // from whichever list is shown. Videos have no format.
  // Super admins only (owner 2026-10-11; format-controls.js): for anyone
  // else there is no format, whatever the area holds.
  const { format, setFormat } = useFormatFilter(dataContext.format, dataContext.setFormat)
  const shownItems = platform && dataContext.filtered ? dataContext.filtered.items : dataContext.items
  const view = useMemo(
    () => {
      if (!format) return platformView
      const keep = new Set(keepFormat(shownItems || [], format, hasFormat(CONTENT_SEARCH)).map(node => node.id))
      return {
        categories: platformView.categories.filter(node => keep.has(node.id)),
        courses: platformView.courses.filter(node => keep.has(node.id)),
        streamingVideos: []
      }
    },
    [platformView, format, shownItems]
  )

  const categories = useMemo(
    () => {
      if (parentID) return view.categories.filter(i => i.parentID === parentID)
      else return view.categories.filter(i => i.parentID === null)
    },
    [view, parentID]
  )

  const courses = useMemo(
    () => {
      if (parentID) return view.courses.filter(i => i.parentID === parentID)
      else return view.courses.filter(i => i.parentID === null)
    },
    [view, parentID]
  )

  const streamingVideos = useMemo(
    () => {
      if (parentID) return view.streamingVideos.filter(i => i.parentID === parentID)
      else return view.streamingVideos.filter(i => i.parentID === null)
    },
    [view, parentID]
  )

  // The section's top is its sidebar entry, its name and its icon
  // (shared/navigation/sections.js); a folder is named after itself, nothing
  // while its name loads, and says it isn't there once the area's list has
  // loaded without it (a deleted folder, an old link: audit F10).
  const folderMissing = !!parentID && dataContext.loaded && !dataContext.error && !category
  const title = useMemo(() => (parentID ? (category ? category.name : folderMissing ? notFoundText('folder') : '') : SECTION.content.text), [category, parentID, folderMissing])

  const Icon = useMemo(
    () => {
      if (parentID) return IconCategory
      else return SECTION.content.Icon
    },
    [parentID]
  )

  const HeaderMenu = () => <FolderMenu place="header" url={url} nodeID={parentID} section={FOLDER_SECTION.CONTENT} />

  // Zero state. The actions are the header menu's own: "add node" (sys admin)
  // and "add category" (org node create), under the same permissions.
  const loginSysAdmin = useLoginSysAdmin()
  const roleOrgNode = useRoleOrgNode()

  const isEmpty = categories.length === 0 && courses.length === 0 && streamingVideos.length === 0

  const platformName = platformLabel(platform)
  const showAllPlatforms = () => dataContext.setPlatform && dataContext.setPlatform(null)
  const formatName = formatLabel(format)
  const showEveryFormat = () => setFormat && setFormat(null)

  const emptyState = folderMissing
    ? folderNotFound(SECTION.content)
    : format && !platform
    ? {
        Icon: IconSearch,
        title: parentID ? `Nothing here is ${formatName}` : `No ${formatName} courses`,
        message: parentID ? `No course in this category is ${formatName}.` : `No course in this organization is ${formatName}.`,
        action: { label: 'Show every format', onClick: showEveryFormat }
      }
    : platform
    ? {
        Icon: IconSearch,
        title: parentID ? `Nothing here runs on ${platformName}` : `No courses run on ${platformName}`,
        message: parentID ? `No course in this category runs on ${platformName} yet.` : `No course in this organization runs on ${platformName} yet.`,
        action: { label: 'Show all platforms', onClick: showAllPlatforms }
      }
    : {
        Icon: parentID ? IconCategory : IconContent,
        title: parentID ? 'This category is empty' : 'No courses yet',
        message: parentID ? 'Courses and categories you add to this category appear here.' : 'Create a course to start building content.',
        action: { label: 'Add course', Icon: IconAdd, to: `${url}/add-type`, show: loginSysAdmin },
        secondary: { label: 'Add a category', to: `${url}/add`, show: !!roleOrgNode.accessCreate }
      }

  const filterError = platform && dataContext.filtered ? dataContext.filtered.error : undefined

  return (
    <React.Fragment>
      <BreadcrumbView Icon={Icon} text={title}>
        <CardFullHeight title={title} HeaderMenu={folderMissing ? undefined : HeaderMenu}>
          <NodeSearchArea
            area={CONTENT_SEARCH}
            nodes={dataContext.items}
            thumbnails={dataContext.thumbnails}
            items={shownItems}
            loaded={loaded}
            error={platform && dataContext.filtered ? filterError : dataContext.error}
            onRetry={platform && dataContext.filtered ? dataContext.filtered.retry : dataContext.retry}
            platform={platform}
            onPlatformChange={dataContext.setPlatform}
            format={format}
            onFormatChange={setFormat}>
            {filterError
              ? <ListError message={`Couldn't load the courses that run on ${platformName}.`} error={filterError} onRetry={dataContext.filtered.retry} />
              : <NodeGridBody loaded={loaded} isEmpty={isEmpty} emptyState={emptyState}>
                  <div className="container-fluid">
                    <div className="node-grid">
                      {categories
                        .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
                        .map((node, i) => <NodeCategoryGridItem key={i} node={node} dataContext={dataContext} section={FOLDER_SECTION.CONTENT} />)}

                      {courses
                        .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
                        .map((node, i) => <NodeContentGridItem key={i} node={node} dataContext={dataContext} />)}

                      {streamingVideos
                        .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
                        .map((node, i) => <NodeContentGridItemVideo key={i} node={node} dataContext={dataContext} />)}
                    </div>
                  </div>
                </NodeGridBody>}
          </NodeSearchArea>
        </CardFullHeight>
      </BreadcrumbView>

      <Route path={`${url}/node/:parentID`}>
        <NodeContentGridBranch grandParentURL={url} />
      </Route>

      <Route path={`${url}/course/:nodeID`}>
        <NodeCourseDetails parentURL={url} />
      </Route>

      <Route path={`${url}/add-type`}>
        <NodeCourseAdd parentURL={url} parentID={parentID} />
      </Route>

      <NodeCategory
        url={url}
        grandParentURL={grandParentURL}
        nodeID={parentID}
        rootName="CONTENT"
        useApiListCategory={useApiListNodeCourseCategory}
        useApiFormCategory={useApiFormNodeCourseCategory}
        useApiListRecycle={useApiListNodeDecendantContent}
        useApiFormRecycle={useApiFormNodeCourse}
        buildPathFn={buildContentCategoryPath}
        dataContext={dataContext}
        section={FOLDER_SECTION.CONTENT}
      />
    </React.Fragment>
  )
}
